import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import jwt_decode from "jwt-decode";
import "./OrderDeliveryDetails.css";

const OrderDeliveryDetails = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  const getOtpFromUrl = () => {
    const otp = searchParams.get("otp");
    return otp && /^\d{5}$/.test(otp) ? otp : null;
  };

  const getUserId = () => {
    try {
      const token = localStorage.getItem("gotasteadmintoken");
      if (token) {
        const decoded = jwt_decode(token);
        return decoded?.UserId || null;
      }
    } catch (e) {
      console.warn("Token decode failed", e);
    }
    return null;
  };

  // ✅ Load once on mount — used for BOTH display AND initial completion check
  useEffect(() => {
    if (!orderId) return;

    setLoading(true);
    axios.get(
      `${process.env.REACT_APP_SECRET}/api/Order/GetOrderDetailsByOrderId`,
      { params: { orderId } }
    )
      .then(res => {
        setOrderDetails(res.data);
      })
      .catch(err => {
        console.warn(err);
        toast.error("Failed to load order details.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [orderId]);

  // ✅ Core action: always uses FRESH data
  const handleMarkAsComplete = async () => {
    if (!orderId) return;

    const otp = getOtpFromUrl();
    if (!otp) {
      toast.error("OTP missing.", { autoClose: 5000, theme: "colored" });
      return;
    }

    setVerifying(true);

    try {
      // 🔁 Fetch FRESH data at time of action
      const res = await axios.get(
        `${process.env.REACT_APP_SECRET}/api/Order/GetOrderDetailsByOrderId`,
        { params: { orderId } }
      );
      const freshData = res.data;

      // If now completed, update UI and exit
      if (freshData.toStatus === 16) {
        setOrderDetails(freshData); // Update state to trigger completion UI
        toast.info("Order already collected.");
        return;
      }

      const subOrder = freshData.subOrders[0];
      if (!subOrder) throw new Error("No sub-order");

      const allReady = subOrder.orderItemDetailViews.every(item =>
        item.toStatusDetails.toLowerCase().includes("ready")
      );

      if (!allReady) {
        toast.error("Move order to ‘Ready’ status before completing.");
        return;
      }

      // Verify OTP
      const userId = getUserId();
      const verifyRes = await axios.post(
        `${process.env.REACT_APP_SECRET}/api/DeliveryPortal/VerifyOTPAndInsertOrderApprovalByRestaurant`,
        {
          orderId: freshData.id,
          subOrderId: subOrder.id,
          otp,
          approverId: userId,
          confirmationMethod: "Qr Code"
        }
      );

      if (
        verifyRes.data === "OTP Not Match" ||
        (typeof verifyRes.data === "string" && verifyRes.data.includes("OTP Not Match"))
      ) {
        throw new Error("Invalid OTP");
      }

      // Complete
      const kitchenIds = subOrder.orderItemDetailViews.map(i => i.kitchenId);
      await axios.post(
        `${process.env.REACT_APP_SECRET}/api/Order/InsertIntoQueueManagementTableByServe`,
        kitchenIds,
        {
          params: {
            fromStatusCode: 6,
            toStatusCode: 17,
            orderId: freshData.id,
            fromTable: "qe_Ready",
            subOrderId: subOrder.id,
            restaurantId: freshData.cartRestaurantId,
          },
        }
      );

      toast.success("✅ Order collected successfully!");

      // Update local state to show completion banner
      setOrderDetails({ ...freshData, toStatus: 16 });

      //setTimeout(() => navigate("/"), 2500);

    } catch (error) {
      console.error("Action failed:", error);
      let msg = "Action failed. Please try again.";
      if (error.message === "Invalid OTP") msg = "Invalid or expired OTP.";
      else if (error.response?.status === 404) msg = "Order not found.";
      toast.error(`🔐 ${msg}`, { autoClose: 5000 });
    } finally {
      setVerifying(false);
    }
  };

  // === RENDER LOGIC ===

  if (loading) {
    return (
      <div className="order-delivery">
        <div className="order-card">
          <div className="order-card-body" style={{ textAlign: "center", padding: "2rem" }}>
            Loading order details...
          </div>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="order-delivery">
        <div className="order-card">
          <div className="order-card-body" style={{ textAlign: "center", padding: "2rem" }}>
            Order not found.
          </div>
        </div>
      </div>
    );
  }

  const subOrder = orderDetails.subOrders[0];
  if (!subOrder) {
    return (
      <div className="order-delivery">
        <div className="order-card">
          <div className="order-card-body" style={{ textAlign: "center", padding: "2rem" }}>
            No sub-order data available.
          </div>
        </div>
      </div>
    );
  }

  // ✅ Show completion banner if already done (from initial load OR after action)
  const isCompleted = orderDetails.toStatus === 16;

  const orderType = (() => {
    const type = subOrder.orderTypeName.toLowerCase();
    if (type.includes("delivery")) return "delivery";
    if (type.includes("dine") || type.includes("in-store")) return "dinein";
    return "collection";
  })();

  const allInstructions = subOrder.orderItemDetailViews
    .map(item => item.instructions.trim())
    .filter(instr => instr && instr !== "N/A");
  const specialInstructions = allInstructions.join("; ") || null;

  const formatTime = (iso) => iso
    ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : "10:30 AM";

  const mapStatus = (status) => {
    const s = status.toLowerCase();
    if (s.includes("ready")) return "ready";
    if (s.includes("preparing")) return "preparing";
    return "pending";
  };

  return (
    <div className="order-delivery">
      <div className="order-delivery__header-section">
        <h1 className="order-delivery__title">Order Management</h1>
        <p className="order-delivery__subtitle">Track and manage orders</p>
      </div>

      <div className="order-card">
        <div className="order-card-header">
          <div>
            <div className="order-id">#{orderDetails.id.replace(/-/g, "").slice(-6)}</div>
            <div className="order-time">{formatTime(subOrder.creationDate)}</div>
          </div>
          <span className={`order-type-badge ${orderType}`}>
            {orderType === "delivery" && "🚗 Delivery"}
            {orderType === "dinein" && "🍽️ Dine In"}
            {orderType === "collection" && "📦 Collection"}
          </span>
        </div>

        <div className="order-card-body">
          <div className="section-title">Order Items</div>
          <ul className="order-items-list">
            {subOrder.orderItemDetailViews.map((item, idx) => (
              <li key={idx} className="order-item">
                <div className="item-info">
                  <span className="item-quantity">{Math.round(item.qty)}x</span>
                  <div>
                    <div className="item-name">{item.itemName}</div>
                    <div className="item-kitchen">{item.kitchenName || "Kitchen"}</div>
                  </div>
                </div>
                <span className={`item-status ${mapStatus(item.toStatusDetails)}`}>
                  {item.toStatusDetails}
                </span>
              </li>
            ))}
          </ul>

          <div className="section-title">
            {orderType === "dinein" ? "Table Details" :
              orderType === "collection" ? "Pickup Details" : "Delivery Details"}
          </div>
          <div className="delivery-details">
            <div className="detail-row">
              <span className="detail-icon">👤</span>
              <div>
                <span className="detail-label">Customer</span>
                <div className="detail-text">{orderDetails.userName}</div>
              </div>
            </div>
            <div className="detail-row">
              <span className="detail-icon">📞</span>
              <div>
                <span className="detail-label">Phone</span>
                <div className="detail-text">{orderDetails.phoneNumber}</div>
              </div>
            </div>
            {orderType === "delivery" && (
              <div className="detail-row">
                <span className="detail-icon">📍</span>
                <div>
                  <span className="detail-label">Address</span>
                  <div className="detail-text">{subOrder.tableNoOrDeliveryAddress}</div>
                </div>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-icon">⏱️</span>
              <div>
                <span className="detail-label">Est. Time</span>
                <div className="detail-text">{subOrder.estimateTime} mins</div>
              </div>
            </div>
          </div>

          {specialInstructions && (
            <div className="special-instructions">
              <div className="instructions-toggle">
                <span className="section-title" style={{ margin: 0 }}>Special Instructions</span>
                <span className="toggle-icon">▼</span>
              </div>
              <div className="instructions-content">{specialInstructions}</div>
            </div>
          )}

          {/* ✅ Show completion banner OR buttons */}
          {isCompleted ? (
            <div className="order-completed-banner">
              <div className="completed-content">
                <div className="completed-checkmark">✓</div>
                <div className="completed-text">
                  <h3>Order Completed</h3>
                  <p>This order has been successfully collected.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="order-card-actions">
              <button
                className="btn-order btn-complete"
                onClick={handleMarkAsComplete}
                disabled={verifying}
              >
                {verifying ? "🔐 Verifying..." : "✓ Mark Complete"}
              </button>
              <button className="btn-order btn-cancel">✕ Cancel</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDeliveryDetails;